import { ReactNode } from 'react';
import InfoCircle from 'assets/icons/InfoCircle';
import Warning from 'assets/icons/Warning';
import CloseCircle from 'assets/icons/CloseCircle';
import CheckMark from 'assets/icons/CheckMark';
import Gear from 'assets/icons/Gear';
import COLOR from 'constants/color';

type AlertSize = 'small' | 'medium' | 'large';
export type AlertVariant = 'info' | 'warning' | 'error' | 'success' | 'neutral';
type AlertStyle = 'subtle' | 'solid' | 'outlined' | 'bordered';

interface AlertProps {
  title?: string;
  message: string;
  description?: string;
  variant?: AlertVariant;
  size?: AlertSize;
  style?: AlertStyle;
  icon?: ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
  actions?: ReactNode;
  className?: string;
  fullWidth?: boolean;
}

const Alert: React.FC<AlertProps> = ({
  title,
  message,
  description,
  variant = 'info',
  size = 'medium',
  style = 'subtle',
  icon,
  dismissible = false,
  onDismiss,
  actions,
  className = '',
  fullWidth = true
}) => {
  // Size classes
  const sizeClasses = {
    small: {
      container: '~p-2/3 ~gap-2/3',
      title: '~text-xs/sm font-semibold',
      text: '~text-[0.625rem]/xs',
      icon: '~w-4/5 ~h-4/5'
    },
    medium: {
      container: '~p-3/4 ~gap-3/4',
      title: '~text-sm/base font-semibold',
      text: '~text-xs/sm',
      icon: '~w-5/6 ~h-5/6'
    },
    large: {
      container: '~p-4/6 ~gap-4/6',
      title: '~text-base/lg font-semibold',
      text: '~text-sm/base',
      icon: '~w-6/7 ~h-6/7'
    }
  };

  // Style variant classes
  const styleClasses = {
    subtle: {
      info: 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800',
      warning: 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800',
      error: 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800',
      success: 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800',
      neutral: 'bg-gray-50 dark:bg-secondary-dark-200/80 border border-gray-200 dark:border-secondary-dark-500'
    },
    solid: {
      info: 'bg-blue-500 dark:bg-blue-600 border border-blue-600 dark:border-blue-700',
      warning: 'bg-yellow-500 dark:bg-yellow-600 border border-yellow-600 dark:border-yellow-700',
      error: 'bg-red-500 dark:bg-red-600 border border-red-600 dark:border-red-700',
      success: 'bg-green-500 dark:bg-green-600 border border-green-600 dark:border-green-700',
      neutral: 'bg-gray-500 dark:bg-secondary-dark-500 border border-gray-600 dark:border-secondary-dark-500'
    },
    outlined: {
      info: 'bg-transparent border-2 border-blue-500 dark:border-blue-400',
      warning: 'bg-transparent border-2 border-yellow-500 dark:border-yellow-400',
      error: 'bg-transparent border-2 border-red-500 dark:border-red-400',
      success: 'bg-transparent border-2 border-green-500 dark:border-green-400',
      neutral: 'bg-transparent border-2 border-gray-500 dark:border-gray-400'
    },
    bordered: {
      info: 'bg-white dark:bg-gray-800 border-l-4 border-blue-500 dark:border-blue-400 border-r border-t border-b border-gray-200 dark:border-gray-700',
      warning: 'bg-white dark:bg-gray-800 border-l-4 border-yellow-500 dark:border-yellow-400 border-r border-t border-b border-gray-200 dark:border-gray-700',
      error: 'bg-white dark:bg-gray-800 border-l-4 border-red-500 dark:border-red-400 border-r border-t border-b border-gray-200 dark:border-gray-700',
      success: 'bg-white dark:bg-gray-800 border-l-4 border-green-500 dark:border-green-400 border-r border-t border-b border-gray-200 dark:border-gray-700',
      neutral: 'bg-white dark:bg-gray-800 border-l-4 border-gray-500 dark:border-gray-400 border-r border-t border-b border-gray-200 dark:border-gray-700'
    }
  };

  // Text color classes
  const textColorClasses = {
    subtle: {
      info: 'text-blue-700 dark:text-blue-300',
      warning: 'text-amber-900 dark:text-amber-200',
      error: 'text-red-700 dark:text-red-300',
      success: 'text-green-700 dark:text-green-300',
      neutral: 'text-gray-700 dark:text-gray-300'
    },
    solid: {
      info: 'text-white',
      warning: 'text-white',
      error: 'text-white',
      success: 'text-white',
      neutral: 'text-white'
    },
    outlined: {
      info: 'text-blue-700 dark:text-blue-300',
      warning: 'text-amber-900 dark:text-amber-200',
      error: 'text-red-700 dark:text-red-300',
      success: 'text-green-700 dark:text-green-300',
      neutral: 'text-gray-700 dark:text-gray-300'
    },
    bordered: {
      info: 'text-gray-900 dark:text-gray-100',
      warning: 'text-gray-900 dark:text-gray-100',
      error: 'text-gray-900 dark:text-gray-100',
      success: 'text-gray-900 dark:text-gray-100',
      neutral: 'text-gray-900 dark:text-gray-100'
    }
  };

  const currentSize = sizeClasses[size];
  const currentStyle = styleClasses[style];
  const currentTextColor = textColorClasses[style];

  // Default icons
  const defaultIcons = {
    info: (
      <InfoCircle
        styles={currentSize.icon}
        fill={COLOR.SEMANTIC.INFO}
        fillDarkMode={COLOR.SEMANTIC.INFO}
      />
    ),
    warning: <Warning styles={currentSize.icon} fill={COLOR.SEMANTIC.WARNING} />,
    error: <CloseCircle styles={currentSize.icon} fill={COLOR.SEMANTIC.ERROR} />,
    success: (
      <CheckMark
        styles={currentSize.icon}
        fill={COLOR.SEMANTIC.SUCCESS}
        fillDarkMode={COLOR.SEMANTIC.SUCCESS}
      />
    ),
    neutral: <Gear styles={currentSize.icon} fill={COLOR.GRAY['700']} />
  };

  const displayIcon = icon || defaultIcons[variant];
  const variantStyle = currentStyle[variant];
  const variantTextColor = currentTextColor[variant];

  return (
    <div
      className={`${fullWidth ? 'w-full' : 'w-fit'} ${currentSize.container} rounded-lg ${variantStyle} flex items-start ~gap-3/4 ${className}`}>
      {/* Icon */}
      <div className={`${currentSize.icon} flex-shrink-0 mt-0.5`}>
        {displayIcon}
      </div>

      {/* Content */}
      <div className='flex-1 flex flex-col ~gap-1/2 min-w-0'>
        {title && (
          <h4 className={`${currentSize.title} ${variantTextColor}`}>
            {title}
          </h4>
        )}
        <p className={`${currentSize.text} ${variantTextColor}`}>
          {message}
        </p>
        {description && (
          <p className={`${currentSize.text} ${variantTextColor} opacity-80`}>
            {description}
          </p>
        )}
        {actions && (
          <div className='flex items-center flex-wrap ~gap-2/3 ~mt-1/2'>
            {actions}
          </div>
        )}
      </div>

      {/* Dismiss Button */}
      {dismissible && onDismiss && (
        <button
          onClick={onDismiss}
          className='flex-shrink-0 ~p-1 hover:bg-black/10 dark:hover:bg-white/10 rounded transition-colors'
          aria-label='Dismiss alert'>
          <CloseCircle
            className={currentSize.icon}
            fill={style === 'solid' ? COLOR.WHITE : COLOR.GRAY['700']}
          />
        </button>
      )}
    </div>
  );
};

export default Alert;

